import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Dna, FlaskConical, Brain, BookOpenText, Sparkles, Plus, ArrowRight, FileUp, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Mendel() {
  const { user } = useAuth();
  const [projects, setProjects] = useState(null);
  const [chroms, setChroms] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/mendel/chromosomes").then(({ data }) => setChroms(data.chromosomes || [])).catch(() => {});
    if (user) api.get("/mendel/projects/mine").then(({ data }) => setProjects(data.projects || [])).catch(() => setProjects([]));
    else setProjects([]);
  }, [user]);

  const startDemo = async () => {
    if (!user) { toast.info("Sign in to open a research project"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/mendel/projects", { title: "demo", from_demo: true });
      window.location.href = `/mendel/ide?project=${data.id}`;
    } catch { toast.error("Could not create demo project"); }
    finally { setBusy(false); }
  };

  return (
    <div className="container-narrow py-10 md:py-14" data-testid="mendel-dashboard">
      <div className="mb-8">
        <div className="label-eyebrow mb-2 inline-flex items-center gap-2 text-marigold">
          <Dna className="w-3 h-3" /> Mendel
        </div>
        <h1 className="font-heading text-4xl md:text-5xl font-semibold">Welcome back, adventurer.</h1>
        <p className="text-ink2 mt-2 max-w-xl">Continue research, explore biology, or ask Mendel AI a question.</p>
      </div>

      {/* Quick tiles */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <Tile to="/mendel/ide" testid="mendel-tile-ide" icon={<FlaskConical className="w-5 h-5" />} title="Open Genome IDE" body="Workbench with the karyogram, gene detail, simulator, and AI." />
        <Tile to="/mendel/ai" testid="mendel-tile-ai" icon={<Brain className="w-5 h-5" />} title="Ask Mendel AI" body="OpenAI-powered research companion. Aware of your workspace." />
        <Tile to="/mendel/lessons/what-is-biological-programming" testid="mendel-tile-lessons" icon={<BookOpenText className="w-5 h-5" />} title="Learn with Ms. Pradhan" body="A 20-lesson computational-genetics curriculum." />
        <button onClick={startDemo} disabled={busy} className="card-soft p-5 text-left hover:shadow-[6px_6px_0_0_#1A1B41] transition disabled:opacity-60" data-testid="mendel-tile-demo">
          <div className="flex items-center gap-2 mb-2 text-marigold">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
            <span className="text-[10px] uppercase tracking-widest font-bold">Demo project</span>
          </div>
          <div className="font-heading text-lg mb-1">Understanding Human Hemoglobin</div>
          <div className="text-ink2 text-xs">Chromosome 11 → HBB → sickle-cell → inheritance — pre-populated.</div>
        </button>
      </div>

      {/* Explore biology */}
      <section className="mb-10">
        <div className="label-eyebrow mb-3">Explore biology · Human chromosomes</div>
        <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-12 gap-2" data-testid="mendel-chrom-grid">
          {chroms.map((c) => (
            <Link
              key={c.id}
              to={`/mendel/chromosome/${c.name}`}
              className="rounded-lg border-2 border-ink bg-cream text-center py-3 hover:bg-marigold hover:text-cream transition group"
              data-testid={`mendel-chrom-tile-${c.name}`}
            >
              <div className="font-heading text-base leading-none">{c.name}</div>
              <div className="text-[9px] uppercase tracking-widest text-ink2 group-hover:text-cream/80 mt-1 font-mono">{c.length_mb.toFixed(0)} Mb</div>
              {c.gene_count > 0 && <div className="text-[9px] text-marigold group-hover:text-cream font-bold mt-0.5">{c.gene_count} gene{c.gene_count > 1 ? "s" : ""}</div>}
            </Link>
          ))}
        </div>
      </section>

      {/* My projects */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <div className="label-eyebrow">Research projects</div>
          <Link to="/mendel/ide" className="text-sm text-marigold font-semibold inline-flex items-center gap-1">
            New notebook <Plus className="w-3.5 h-3.5" />
          </Link>
        </div>
        {projects === null ? (
          <div className="text-center py-8 text-ink2"><Loader2 className="w-4 h-4 mx-auto animate-spin" /></div>
        ) : projects.length === 0 ? (
          <div className="card-soft p-6 text-center text-ink2 text-sm" data-testid="mendel-projects-empty">
            Biology is waiting.  Begin by exploring a gene, opening a chromosome, uploading a dataset or asking Ms. Pradhan a question.
          </div>
        ) : (
          <div className="space-y-2" data-testid="mendel-projects-list">
            {projects.map((p) => (
              <Link key={p.id} to={`/mendel/ide?project=${p.id}`} className="block card-soft p-4 hover:shadow-[4px_4px_0_0_#1A1B41] transition" data-testid={`mendel-project-${p.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-heading text-lg font-semibold truncate">{p.title}</div>
                    {p.abstract && <div className="text-ink2 text-xs mt-1 line-clamp-2">{p.abstract}</div>}
                  </div>
                  <ArrowRight className="w-4 h-4 text-ink2 shrink-0" />
                </div>
                <div className="mt-2 text-[10px] uppercase tracking-widest font-mono text-ink2">{(p.notebook || []).length} entries · updated {new Date(p.updated_at).toLocaleDateString()}</div>
              </Link>
            ))}
          </div>
        )}
      </section>

      <div className="mt-10 text-center text-xs text-ink2 max-w-lg mx-auto">
        Mendel is an educational and research computational-biology environment.
        It does not provide medical advice or operational wet-lab instructions.
      </div>
    </div>
  );
}

function Tile({ to, testid, icon, title, body }) {
  return (
    <Link to={to} className="card-soft p-5 hover:shadow-[6px_6px_0_0_#1A1B41] transition block" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2 text-marigold">
        {icon}
        <span className="text-[10px] uppercase tracking-widest font-bold">Open</span>
      </div>
      <div className="font-heading text-lg mb-1">{title}</div>
      <div className="text-ink2 text-xs leading-relaxed">{body}</div>
    </Link>
  );
}
